import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PricingCard from './PricingCard';
import { PLAN_CONFIG, PLAN_ORDER } from '../config/plans';
import { useAuth } from '../context/AuthContext';
import billingService from '../services/billingService';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
};

const PricingSection = ({
  showHeader = true,
  onAuthRequired,
  variant = 'default', // 'default' | 'compact' | 'full'
  className = ''
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, sessionUser } = useAuth();
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [loading, setLoading] = useState(null);

  const currentPlan = sessionUser?.plan_type || 'free';

  const displayPlans = variant === 'compact'
    ? ['free', 'pro']
    : PLAN_ORDER;

  const handleSelectPlan = async (plan, billingInterval) => {
    // Free plan - go to dashboard or show auth
    if (plan.id === 'free') {
      if (isAuthenticated) {
        navigate('/dashboard');
      } else if (onAuthRequired) {
        onAuthRequired();
      }
      return;
    }

    // Enterprise - contact sales
    if (plan.id === 'enterprise') {
      window.location.href = 'mailto:hello@duckpilot.ai?subject=Enterprise%20Inquiry';
      return;
    }

    // Pro/Team - require auth first
    if (!isAuthenticated) {
      if (onAuthRequired) {
        onAuthRequired();
      }
      return;
    }

    // Already on this plan
    if (currentPlan === plan.id) {
      return;
    }

    // Start checkout
    setLoading(plan.id);
    try {
      const session = await billingService.createCheckoutSession(
        billingInterval,
        plan.id
      );
      window.location.href = session.url;
    } catch (error) {
      console.error('Unable to start checkout:', error);
    } finally {
      setLoading(null);
    }
  };

  return (
    <section className={`${className}`}>
      {showHeader && (
        <motion.div {...fadeUp} className="max-w-2xl mb-12">
          <span className="text-sm font-medium text-bill-600 mb-4 block">Pricing</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-ink font-semibold">
            Simple, transparent pricing
          </h2>
          <p className="mt-4 text-xl text-sand-700">
            Start free, upgrade when you need more. No hidden fees.
          </p>
        </motion.div>
      )}

      {/* Billing toggle */}
      <motion.div
        {...fadeUp}
        className="flex items-center justify-center gap-2 mb-12"
      >
        <div className="inline-flex rounded-full bg-sand-100 border border-[rgba(32,30,29,0.12)] p-1">
          <button
            type="button"
            onClick={() => setBillingInterval('monthly')}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              billingInterval === 'monthly'
                ? 'bg-white text-ink shadow-brand-sm'
                : 'text-sand-700 hover:text-ink'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval('yearly')}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              billingInterval === 'yearly'
                ? 'bg-white text-ink shadow-brand-sm'
                : 'text-sand-700 hover:text-ink'
            }`}
          >
            Yearly
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              billingInterval === 'yearly'
                ? 'bg-sage-500 text-white'
                : 'bg-sage-100 text-sage-800'
            }`}>
              -20%
            </span>
          </button>
        </div>
      </motion.div>

      {/* Pricing cards */}
      <div
        className={`grid gap-5 ${
          variant === 'compact'
            ? 'md:grid-cols-2'
            : variant === 'full'
            ? 'md:grid-cols-2 lg:grid-cols-4'
            : 'md:grid-cols-2 lg:grid-cols-4'
        }`}
      >
        {displayPlans.map((planId) => (
          <PricingCard
            key={planId}
            plan={PLAN_CONFIG[planId]}
            interval={billingInterval}
            onSelect={handleSelectPlan}
            currentPlan={currentPlan}
            loading={loading === planId}
            isAuthenticated={isAuthenticated}
          />
        ))}
      </div>

      {/* FAQ / Trust signals */}
      <motion.div
        {...fadeUp}
        className="mt-16 grid gap-4 md:grid-cols-3 text-center"
      >
        <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 hover:bg-sand-100 transition-colors shadow-brand-sm">
          <h4 className="font-semibold text-ink mb-2">No credit card required</h4>
          <p className="text-sm text-sand-600">Start with the free plan instantly</p>
        </div>
        <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 hover:bg-sand-100 transition-colors shadow-brand-sm">
          <h4 className="font-semibold text-ink mb-2">Cancel anytime</h4>
          <p className="text-sm text-sand-600">No long-term contracts or commitments</p>
        </div>
        <div className="rounded-2xl border border-[rgba(32,30,29,0.12)] bg-white p-6 hover:bg-sand-100 transition-colors shadow-brand-sm">
          <h4 className="font-semibold text-ink mb-2">Secure payments</h4>
          <p className="text-sm text-sand-600">Powered by Stripe for peace of mind</p>
        </div>
      </motion.div>
    </section>
  );
};

export default PricingSection;
